<template>
  <div class="modal_wrapper active">
    <div class="modal_window white_box create_report_modal">
      <div class="heading_wrapper">
        <h3 class="heading">
          Here are some potential competitors
        </h3>
        <button
          class="close_modal close_btn"
          @click="closeModal()"
        >
          <svg
            fill="none"
            height="32"
            viewBox="0 0 32 32"
            width="32"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M25 7L7 25"
              stroke="#040707"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            />
            <path
              d="M25 25L7 7"
              stroke="#040707"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            />
          </svg>
        </button>
      </div>

      <div class="desc">
        Review the competitor listed below and try to select those who are most similar to your business,
        as these will provide the best new keyword suggestions. These websites are based on domains that
        we think might be your top competitors.
      </div>

      <div
        v-if="suggestedCompetitors.length > 1"
        class="more_button"
      >
        <span
          class="original_suggestions"
          @click="originalSuggestions"
        >
          Back to other suggestions
        </span>
      </div>

      <accordion-transition>
        <div
          v-if="competitorUrl"
          class="confirmation"
        >
          We've added {{ competitorUrl }} to your competitors
        </div>
      </accordion-transition>
      <accordion-transition>
        <div
          v-if="error"
          class="error"
          v-html="error"
        />
      </accordion-transition>

      <table class="competitors_table head">
        <thead>
          <tr>
            <td>
              Competitor
            </td>
            <td>
              Visibility Rating
              <i
                class="far fa-question-circle explanation"
                @click="showExplanation"
              />
            </td>
            <td>
              &nbsp;
            </td>
          </tr>
        </thead>
      </table>
      <div class="table_body_wrap">
        <table class="competitors_table">
          <tbody>
            <tr
              v-for="(competitor, index) in limitedSuggestions"
              :key="index"
            >
              <td class="keyword">
                {{ competitor.domain }}
              </td>
              <td class="difficulty">
                <span :class="''+getVisibilityClass(competitor.visibility)">
                  {{ getVisibilityName(competitor.visibility) }}
                </span>
              </td>
              <td>
                <button
                  :disabled="loading"
                  class="row_button"
                  @click="add(competitor, index)"
                >
                  <i
                    v-if="addingCompetitor === index"
                    class="fas fa-spinner fa-spin"
                  />
                  <svg
                    v-else
                    fill="none"
                    height="14"
                    viewBox="0 0 14 14"
                    width="14"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.1875 7H11.8125"
                      stroke="#37B2A8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                    />
                    <path
                      d="M7 2.1875V11.8125"
                      stroke="#37B2A8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                    />
                  </svg>
                  <span v-html="addingCompetitor === index ? 'Adding' : 'Add competitor'" />
                </button>
              </td>
            </tr>
            <tr>
              <td
                v-if="serpSuggestedCompetitors.length === 0"
                class="no_results text-center"
                colspan="3"
              >
                Sorry, we couldn't find any suggested competitors for your website
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import {mapActions, mapState} from 'vuex';
import AccordionTransition from './../../../../reports/components/global/transitions/AccordionTransition.vue';

export default {
  components: {
    AccordionTransition
  },
  data() {
    return {
      bulkKeywords: '',
      loading: false,
      visibleRows: 20,
      addingCompetitor: null,
      competitorUrl: null,
      showCompetitorKeywordsModal: false,
      error: null,
    }
  },
  computed: {
    ...mapState('competition', [
      'suggestedCompetitors',
      'serpSuggestedCompetitors',
    ]),
    limitedSuggestions() {
      return this.serpSuggestedCompetitors.slice(0, this.visibleRows);
    }
  },
  mounted() {
    let table = document.querySelector('.table_body_wrap');
    table.onscroll = () => {
      let scrollTop = table.scrollTop;
      let scrollHeight = table.scrollHeight;
      let clientHeight = table.clientHeight;
      let scrollPercent = (scrollTop / (scrollHeight - clientHeight)) * 100;
      if (scrollPercent > 90) {
        this.visibleRows += 50;
      }
    };
  },
  methods: {
    ...mapActions('keywordIdeas', [
        'fetchSuggestions',
        'setFilter'
    ]),
    ...mapActions('competition', [
        'addCompetitor',
        'closeSerpSuggestedCompetitorsModal',
        'openSuggestedCompetitorsModal',
    ]),
    // TODO: Need to establish accurate scoring for visibility.
    getVisibilityClass(visibility) {
      if (visibility <= 5) return 'low';
      if (visibility <= 10) return 'medium';
      if (visibility <= 15) return 'high';
      return 'very_high';
    },
    getVisibilityName(visibility) {
      if (visibility <= 5) return 'Low';
      if (visibility <= 10) return 'Medium';
      if (visibility <= 15) return 'High';
      return 'Very High';
    },
    showExplanation(){
      Swal.fire({
        icon: 'info',
        title: 'Visibility Rating',
        html: 'This value represents the overlap between you and your competitor&apos;s websites for '+
          'the keywords your website appears for in Google. The values range from low to very high',
      })
    },
    add(competitor, index) {
      this.loading = true;
      this.addingCompetitor = index;
      let domain = competitor.domain.replace('www.', '');
      this.addCompetitor(`https://${domain}/`)
          .then((response) => {
            this.competitorUrl = competitor.domain;
            this.addingCompetitor = null;
            this.loading = false;
            this.fetchSuggestions().then(() => {
              this.setFilter({key: 'competitorFilter', value: response.simple_url});
            });
            setTimeout(() => {
              this.competitorUrl = null;
            }, 3000);
          })
          .catch(response => {
            this.loading = false;
            this.addingCompetitor = null;
            const error = response.data;
            if (response.status === 422) {
              this.error = error.message;
              return;
            }

            let {title, message} = error;
            if (response.status >= 500) {
              title = 'Whoops!';
              message = 'Something went wrong. Please try again.';
            }

            Swal.fire({
              icon: 'warning',
              title: title,
              html: message,
            });
          });
    },
    originalSuggestions() {
      this.closeSerpSuggestedCompetitorsModal();
      this.openSuggestedCompetitorsModal();
    },
    closeModal() {
      this.closeSerpSuggestedCompetitorsModal();
    }
  }
}
</script>

<style scoped>
.modal_wrapper {
  z-index: 100;
  position: fixed;
  display: flex;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  -webkit-transition: .3s;
  -o-transition: .3s;
  transition: .3s;
  padding: 5vh 0;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.2);
}

.modal_wrapper > .modal_wrapper {
  z-index: 101;
}

.modal_wrapper::-webkit-scrollbar {
  width: 0;
}

.modal_window {
  position: relative;
  margin: auto;
  max-width: 900px;
  width: 90vw;
  z-index: 100;
  -webkit-box-shadow: 0px 10px 30px rgba(4, 7, 7, 0.2);
  box-shadow: 0px 10px 30px rgba(4, 7, 7, 0.2);
  border-radius: .75rem;
}

.heading_wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.close_btn {
  height: auto;
}

.heading {
  font-weight: 700;
  line-height: 1.42;
  letter-spacing: -0.042em;
  color: #040707;
  font-size: 1.5rem;
}

.desc {
  margin-bottom: 10px;
}

.more_button {
  display: flex;
  justify-content: flex-end;
}

.original_suggestions {
  cursor: pointer;
  color: var(--primary-color);
  font-size: 12px;
  float: right;
}

.confirmation {
  background: #DFF5EE;
  font-size: .8rem;
  margin: 5px auto;
  border-radius: 3px;
  padding: 5px 10px;
  transition: .3s;
}

.error {
  font-size: .8rem;
  margin: 5px auto;
  border-radius: 3px;
  padding: 5px 10px;
  transition: .3s;
  margin-bottom: 10px;
  background: #EBBFBE;
}

.competitors_table {
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;
}

.competitors_table.head {
  width: calc(100% - 15px);
  height: 15%;
}

.competitors_table thead td {
  padding: 1rem 1rem 1rem;
}

.competitors_table thead td {
  font-weight: 500;
  font-size: 16px;
  line-height: 26px;
  letter-spacing: -0.25px;
  margin-right: 0.25rem;
}

.competitors_table thead td .explanation {
  cursor: pointer;
}

.table_body_wrap {
  width: 100%;
  overflow-y: scroll;
  max-height: 60vh;
}

.sort_title {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.sort_title.center {
  justify-content: center;
}

.sort_title.desc i {
  transform: rotate(180deg);
}

.competitors_table tr td:first-of-type {
  width: 55%;
}

.competitors_table tr td:nth-of-type(2) {
  width: 25%;
  text-align: center;
}

.competitors_table tr td:nth-of-type(3) {
  padding: 0;
  text-align: center;
}

.competitors_table tbody tr:nth-of-type(2n+1) {
  background: #FBFAFA;
}

.competitors_table tbody tr td {
  padding: 1rem;
}

.matching_keywords {
  background-color: #E4FAFD;
  color: #1993AE;
  padding: 0.25rem 0.5rem;
  font-size: 10px;
  line-height: 1.3;
  letter-spacing: -0.1px;
  border-radius: 6px;
  margin: 2px;
  cursor: pointer;
}

.row_button {
  height: 30px;
  cursor: pointer;
  border-radius: 6px;
  border: 2px solid #37B2A8;
  color: #37B2A8;
  margin-right: 1rem;
  line-height: 1.4rem;
}

.row_button svg {
  margin-top: -0.1rem;
}

.row_button .fas,
.row_button svg {
  margin-right: 0.5rem;
}
</style>
